import { useCallback } from 'react';

import { queryOptions, useQuery, useQueryClient } from '@tanstack/react-query';

import { getAccessToken } from '@/lib/tokenStorage';

import { CsrItem } from '../lookups/types';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;

export type AccountResponse = {
  id: string;
  code: string | null;
  name: string | null;
  emailAddress1?: string | null;
  phoneNumber?: string | null;
  ranchAddress?: {
    line1?: string | null;
    line2?: string | null;
    line3?: string | null;
    city?: string | null;
    stateOrProvince?: string | null;
    postalCode?: string | null;
    composite?: string | null;
  } | null;
  billingAddress?: {
    line1?: string | null;
    line2?: string | null;
    line3?: string | null;
    city?: string | null;
    stateOrProvince?: string | null;
    postalCode?: string | null;
    composite?: string | null;
  } | null;
  primaryContact?: {
    id: string;
    fullName: string | null;
    firstName?: string | null;
    lastName?: string | null;
    emailAddress1?: string | null;
    mobilePhone?: string | null;
    telephone1?: string | null;
    telephone2?: string | null;
    telephone3?: string | null;
  } | null;
  primaryContactId?: string | null;
  customerServiceRepresentativeId: string | null;
  customerServiceRepresentative: CsrItem | null;
  aging60: number | null;
  aging90: number | null;
  aging120: number | null;
  aging180: number | null;
  aging365: number | null;
  tog90daysPastDue: number | null;
  tog90DayOverrideExpireDate: string | null;
  stateCode: number;
  accountStatus: number | null;
  statusCode?: number | null;
  priceTier: number | null;
  primaryTextingNumber: string | null;
  isPwp: boolean | null;
};

type AccountsPaginatedResponse = {
  items: AccountResponse[];
  hasMore: boolean;
};

function getBaseUrl(): string {
  const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;
  if (
    !API_BASE_URL ||
    API_BASE_URL === 'undefined' ||
    API_BASE_URL === 'null'
  ) {
    throw new Error('VITE_API_BASE_URL is not defined or invalid');
  }
  return API_BASE_URL;
}

function getHeaders(): HeadersInit {
  const token = getAccessToken();
  if (!token) {
    throw new Error('No access token available. Please log in.');
  }
  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
  };
}

async function fetchAccounts(params: {
  search?: string;
  skip?: number;
  take?: number;
  id?: string;
}): Promise<AccountsPaginatedResponse> {
  const token = getAccessToken();
  const url = new URL('/api/v1/account', API_BASE_URL);

  if (params.search?.trim()) {
    url.searchParams.set('search', params.search.trim());
  }
  if (params.id) {
    url.searchParams.set('id', params.id);
  }

  if (params.skip != null) {
    url.searchParams.set('skip', String(params.skip));
  }
  if (params.take != null) {
    url.searchParams.set('take', String(params.take));
  }

  url.searchParams.append('order', 'code');
  url.searchParams.append('order', 'name');

  const response = await fetch(url, {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${token}`,
    },
    credentials: 'include',
  });

  if (!response.ok) {
    throw new Error('Failed to fetch accounts');
  }

  return (await response.json()) as AccountsPaginatedResponse;
}

export function useSearchAccountsLazy() {
  const queryClient = useQueryClient();

  const search = useCallback(
    async ({
      search,
      selectionLimit,
    }: {
      search: string;
      selectionLimit: number;
    }): Promise<{ data: AccountResponse[] }> => {
      const data = await queryClient.fetchQuery(
        searchAccountsOptions({ search, selectionLimit }),
      );
      return { data };
    },
    [queryClient],
  );

  return [search] as const;
}

async function fetchAccountsAutocomplete({
  search,
  selectionLimit,
}: {
  search: string;
  selectionLimit: number;
}): Promise<AccountResponse[]> {
  const searchParams = new URLSearchParams();
  if (search) {
    searchParams.set('search', search);
  }
  searchParams.set('selectionLimit', String(selectionLimit));

  const url = `${getBaseUrl()}/api/v1/account/autocomplete?${searchParams.toString()}`;

  const response = await fetch(url, {
    method: 'GET',
    credentials: 'include',
    headers: getHeaders(),
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  return (await response.json()) as AccountResponse[];
}

export const searchAccountsOptions = ({
  search,
  selectionLimit,
}: {
  search: string;
  selectionLimit: number;
}) =>
  queryOptions({
    queryKey: ['lookups', 'accounts', 'search', { search, selectionLimit }],
    async queryFn() {
      return fetchAccountsAutocomplete({ search, selectionLimit });
    },
  });

export function accountsQueryOptions(params: {
  search?: string;
  skip?: number;
  take?: number;
}) {
  return queryOptions({
    queryKey: ['accounts', params],
    async queryFn() {
      const result = await fetchAccounts(params);
      return result.items;
    },
  });
}

export function accountByIdQueryOptions(id: string | undefined) {
  return queryOptions({
    queryKey: ['accounts', 'byId', id],
    enabled: !!id,
    async queryFn() {
      const result = await fetchAccounts({ id, take: 1 });
      return result.items[0] ?? null;
    },
  });
}

export function useAccountByIdQuery(id: string | undefined) {
  return useQuery(accountByIdQueryOptions(id));
}

export type AdvancedAccountsResponse = {
  items: AccountResponse[];
  totalCount: number;
};

export const fetchAdvancedAccounts = async (params: {
  search?: string;
  skip?: number;
  take?: number;
  order?: string;
  filter?: string | null;
  addressFilter?: string | null;
  v2?: boolean;
}): Promise<AdvancedAccountsResponse> => {
  const url = new URL(
    params.v2 ? '/api/v2/account' : '/api/v1/account',
    getBaseUrl(),
  );

  if (params.search?.trim()) {
    url.searchParams.set('search', params.search.trim());
  }
  if (params.skip != null) {
    url.searchParams.set('skip', String(params.skip));
  }
  if (params.take != null) {
    url.searchParams.set('take', String(params.take));
  }
  if (params.order) {
    url.searchParams.set('order', params.order);
  }
  if (params.filter) {
    url.searchParams.set('$filter', params.filter);
  }
  if (params.addressFilter) {
    url.searchParams.set('$addressFilter', params.addressFilter);
  }

  const response = await fetch(url, {
    method: 'GET',
    credentials: 'include',
    headers: getHeaders(),
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  return (await response.json()) as AdvancedAccountsResponse;
};
